import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/router";
import {
  MessageSquare,
  X,
  Zap,
  Lock,
  Loader2,
  AlertCircle,
  Instagram,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/theme-toggle";
import { MobileNav } from "@/components/mobile-nav";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { useCredits } from "@/hooks/use-credits";
import { useAccountQuota } from "@/hooks/use-account-quota";
import { copyPublicAccountToSaved } from "@/lib/saved-accounts-service";
import { ProfileCarousel } from "@/components/profile-carousel/profile-carousel";
import { Preloader } from "@/components/ui/preloader";

// 애니메이션 스티프니스 값 정의
const a300 = 300;

// Quota Exceeded Dialog Component
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Types
interface QuotaExceededDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentPlan?: string;
}

interface CopyPublicAccountResult {
  success: boolean;
  message: string;
  quotaExceeded?: boolean;
  currentPlan?: string;
  saved_id?: string;
}

interface CarouselProfile {
  id: string;
  username: string;
  profileImageUrl?: string;
  postImageUrl?: string;
  followerCount?: number;
  fullName?: string;
  biography?: string;
  locked?: boolean;
}

interface InstantChatAccount {
  id: string;
  username: string;
  profile_data: {
    profilePicUrl?: string;
    profilePicUrlHD?: string;
    fullName?: string;
    biography?: string;
    followersCount?: number;
    postsCount?: number;
    followsCount?: number | boolean;
    isVerified?: boolean;
  };
  biography?: string;
  followersCount?: number;
  followingCount?: number;
  supabase_profile_pic_url?: string;
  supabase_profile_hd_pic_url?: string;
  supabase_latest_post_url?: string;
  persona_id?: string;
  persona_name?: string;
  display_name?: string;
  business_category?: string;
  profile_website?: string;
  profile_color?: string;
  profile_theme?: string;
}

interface GridAccountsPageData {
  accounts: InstantChatAccount[];
  nextPage?: number;
}

function QuotaExceededDialog({
  open,
  onOpenChange,
  currentPlan = "free",
}: QuotaExceededDialogProps) {
  const navigate = useNavigate();

  const planDetails = {
    free: {
      title: "무료 계정 제한",
      limit: "2개",
      message:
        "무료 계정은 두 개의 인스타그램 계정만 연결할 수 있습니다. 더 많은 계정을 연결하려면 유료 구독이 필요합니다.",
    },
    basic: {
      title: "베이직 플랜 제한",
      limit: "10개",
      message:
        "베이직 구독은 10개의 인스타그램 계정을 연결할 수 있습니다. 더 많은 계정을 연결하려면 프리미엄 구독으로 업그레이드하세요.",
    },
    premium: {
      title: "계정 제한 오류",
      limit: "무제한",
      message:
        "프리미엄 구독에서는 계정 제한이 없어야 합니다. 이 오류가 계속되면 고객 지원에 문의하세요.",
    },
  };

  const details =
    planDetails[currentPlan as keyof typeof planDetails] || planDetails.free;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md md:max-w-md lg:max-w-md">
        <AlertDialogHeader className="mb-4 space-y-2">
          <AlertDialogTitle className="flex items-center text-xl md:text-2xl text-destructive gap-3">
            <AlertCircle className="h-6 w-6 flex-shrink-0" />
            <span>{details.title}</span>
          </AlertDialogTitle>
          <AlertDialogDescription className="text-base leading-relaxed">
            현재 인스타그램 계정 연결 제한인 {details.limit}에 도달했습니다.
            <br className="hidden md:block" />
            {details.message}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-4 mt-6 md:mt-8 pt-2">
          <AlertDialogCancel className="mt-2 sm:mt-0 h-12 text-base">
            닫기
          </AlertDialogCancel>
          <Button
            className="gradient-button w-full sm:w-auto h-12 text-base font-medium"
            onClick={() => {
              onOpenChange(false);
              navigate("/pricing");
            }}
          >
            <Zap className="mr-2 h-5 w-5" />
            요금제 보기
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// 이전에 만들었던 버튼 컴포넌트
interface BottomButtonsProps {
  handleStartChat: () => void;
  isSaving: boolean;
  session: any; // 실제 세션 타입으로 변경하세요
  selectedProfile: { username: string }; // 실제 프로필 타입으로 변경하세요
  openInstagramProfile: () => void;
}

function BottomButtons({
  handleStartChat,
  isSaving,
  session,
  openInstagramProfile, // 함수를 prop으로 받도록 수정
}: BottomButtonsProps) {
  return (
    <motion.div
      className="w-full pt-4" // bg-background 제거 (부모에서 제어)
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.7 }}
    >
      {/* 첫 번째 줄: 대화 시작하기 버튼 */}
      <div className="mb-3"> {/* px-4 sm:px-0 제거 (부모에서 제어) */}
        <Button
          onClick={handleStartChat}
          className="w-full gradient-button p-3 text-base font-semibold overflow-hidden group relative"
          disabled={isSaving}
          size="lg"
        >
          <motion.span
            className="absolute inset-0 bg-white/20 transform -translate-x-full transition-transform duration-300 group-hover:translate-x-0"
            initial={false}
          />
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              처리 중...
            </>
          ) : (
            <>
              <MessageSquare className="mr-2 h-5 w-5 animate-pulse" />
              {session ? "대화 시작하기" : "로그인하고 대화하기"}
            </>
          )}
        </Button>
      </div>

      {/* 두 번째 줄: 인스타그램 바로가기 버튼 */}
      <div className="mb-3"> {/* px-4 sm:px-0 제거 (부모에서 제어) */}
        <Button
          onClick={openInstagramProfile}
          variant="outline"
          className="w-full p-3 text-base font-semibold overflow-hidden group relative border-pink-400 hover:bg-pink-50/10 transition-all duration-300"
          size="lg"
        >
          <Instagram className="mr-2 h-5 w-5 text-pink-500" />
          인스타그램 바로가기
        </Button>
      </div>
    </motion.div>
  );
}

/**
 * 이미지 URL이 유효한지 확인하는 함수
 * Instagram 도메인 URL이나 기본 이미지인 경우 false 반환
 */
const isValidImageUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;

  // URL이 비어있거나 undefined인 경우
  if (url.trim() === "") return false;

  // 인스타그램 직접 URL인 경우 필터링 (CORS 이슈 발생)
  if (url.includes("instagram.") || url.includes("fbcdn.net")) return false;

  // imagekit.io 주소인 경우만 허용
  return url.includes("imagekit.io");
};

export function InstantChatPage() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { availableCredits } = useCredits();
  const { data: quotaInfo, isLoading: isLoadingQuota } = useAccountQuota();
  const [selectedProfile, setSelectedProfile] =
    useState<InstantChatAccount | null>(null);
  const [showProfileDetail, setShowProfileDetail] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showQuotaExceededAlert, setShowQuotaExceededAlert] = useState(false);
  const [currentPlan, setCurrentPlan] = useState<string>("free");
  const [recentPosts, setRecentPosts] = useState<
    {
      imagekit_url: string;
      id: string;
      caption?: string;
      likes_count?: number;
      timestamp?: string;
      isVisible?: boolean;
    }[]
  >([]);
  const [isLoadingPosts, setIsLoadingPosts] = useState<boolean>(false);
  const location = useLocation();

  useEffect(() => {
    const locationState = location.state as {
      directChat?: boolean;
      username?: string;
      profile?: Record<string, unknown>;
    } | null;

    if (locationState?.directChat && locationState.username) {
      console.log("Direct chat request with account:", locationState.username);
      const profileData = locationState.profile || {};
      setSelectedProfile({
        id: `temp_${Date.now()}`,
        username: locationState.username,
        biography:
          typeof profileData.biography === "string"
            ? profileData.biography
            : "",
        followersCount:
          typeof profileData.followersCount === "number"
            ? profileData.followersCount
            : 0,
        followingCount:
          typeof profileData.followsCount === "number"
            ? profileData.followsCount
            : 0,
        profile_data: profileData,
        supabase_profile_pic_url:
          typeof profileData.supabase_profile_pic_url === "string"
            ? profileData.supabase_profile_pic_url
            : "",
        display_name: locationState.username,
      });
      setShowProfileDetail(true);
      navigate("/", { replace: true, state: null });
    }
  }, [location.state, navigate]);

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadMoreClickCount, setLoadMoreClickCount] = useState(0);
  const observerTarget = useRef<HTMLDivElement>(null);
  const [localHasNextPage, setLocalHasNextPage] = useState(true);

  const MAX_ACCOUNTS_FOR_GUESTS = 12;
  const FULLY_VISIBLE_ACCOUNTS_FOR_GUESTS = 6;

  const { data: carouselAccounts, isLoading: isCarouselLoading } = useQuery<
    InstantChatAccount[]
  >({
    queryKey: ["carouselInstagramAccounts"],
    queryFn: async (): Promise<InstantChatAccount[]> => {
      try {
        console.log("캐로셀 계정 정보 불러오기 시작");
        const { data, error } = await supabase
          .from("public_instagram_accounts")
          .select(
            "id, username, display_name, supabase_profile_pic_url, supabase_profile_hd_pic_url, supabase_latest_post_url, profile_data, persona_id, business_category"
          )
          .not("supabase_latest_post_url", "is", null)
          .not("supabase_profile_pic_url", "is", null)
          .ilike("supabase_profile_pic_url", "%imagekit%")
          .order("updated_at", { ascending: false })
          .limit(10);
        if (error) throw new Error(error.message);
        console.log(`캐로셀 계정 ${data?.length || 0}개 불러오기 성공`);
        return data || [];
      } catch (error) {
        // 오류 로그 출력 제거
        // console.error("캐로셀 계정 조회 실패:", error);
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: gridAccounts,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<GridAccountsPageData>({
    queryKey: ["gridInstagramAccounts"],
    queryFn: async ({ pageParam = 1 }) => {
      const from = (Number(pageParam) - 1) * 10;
      const to = from + 10 - 1;
      try {
        const { data, error, count } = await supabase
          .from("public_instagram_accounts")
          .select(
            "id, username, display_name, supabase_profile_pic_url, supabase_profile_hd_pic_url, supabase_latest_post_url, profile_data, persona_id, business_category",
            { count: "exact" }
          )
          .not("id", "is", null)
          .not("supabase_profile_pic_url", "is", null)
          .ilike("supabase_profile_pic_url", "%imagekit%")
          .order("updated_at", { ascending: false })
          .range(from, to);
        if (error) throw new Error(error.message);
        const accountsData = data || [];
        const hasMore =
          count !== null ? from + accountsData.length < count : false;
        return {
          accounts: accountsData,
          nextPage: hasMore ? Number(pageParam) + 1 : undefined,
        };
      } catch (error) {
        // 오류 로그 출력 제거
        // console.error("그리드 계정 조회 실패:", error);
        throw error;
      }
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: GridAccountsPageData): number | undefined =>
      lastPage.nextPage,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (hasNextPage !== undefined) {
      setLocalHasNextPage(hasNextPage);
    }
  }, [hasNextPage]);

  const handleLoadMore = useCallback(() => {
    if (isFetchingNextPage || isLoadingMore || !localHasNextPage) return;
    if (!session) {
      const newClickCount = loadMoreClickCount + 1;
      setLoadMoreClickCount(newClickCount);
      const accountsLength: number =
        gridAccounts?.pages?.flatMap((page) => page.accounts)?.length || 0;
      if (accountsLength < MAX_ACCOUNTS_FOR_GUESTS && newClickCount < 2) {
        setIsLoadingMore(true);
        fetchNextPage().finally(() => setIsLoadingMore(false));
      } else {
        toast({
          title: "회원가입 필요",
          description: "더 많은 계정을 보려면 회원가입이 필요합니다.",
        });
        navigate("/auth", { state: { returnTo: "/instant-chat" } });
      }
    } else {
      setIsLoadingMore(true);
      fetchNextPage().finally(() => setIsLoadingMore(false));
    }
  }, [
    fetchNextPage,
    isFetchingNextPage,
    isLoadingMore,
    localHasNextPage,
    gridAccounts,
    loadMoreClickCount,
    navigate,
    session,
    toast,
  ]);

  useEffect(() => {
    const options = { root: null, rootMargin: "0px", threshold: 0.5 };
    const observer = new IntersectionObserver((entries) => {
      if (
        entries[0].isIntersecting &&
        localHasNextPage &&
        !isFetchingNextPage &&
        !isLoadingMore
      ) {
        handleLoadMore();
      }
    }, options);
    const currentObserverTarget = observerTarget.current;
    if (currentObserverTarget) observer.observe(currentObserverTarget);
    return () => {
      if (currentObserverTarget) observer.unobserve(currentObserverTarget);
    };
  }, [localHasNextPage, isFetchingNextPage, isLoadingMore, handleLoadMore]);

  const carouselProfiles: CarouselProfile[] =
    carouselAccounts && carouselAccounts.length > 0
      ? carouselAccounts
        .filter(account =>
          isValidImageUrl(account.supabase_profile_pic_url) &&
          isValidImageUrl(account.supabase_latest_post_url)
        )
        .map((account, index) => ({
          id: account.id,
          username: account.username,
          profileImageUrl:
            account.supabase_profile_pic_url ||
            account.profile_data?.profilePicUrl,
          postImageUrl: account.supabase_latest_post_url,
          followerCount: account.profile_data?.followersCount,
          fullName: account.display_name || account.profile_data?.fullName,
          biography: account.profile_data?.biography,
          locked: !session && index >= FULLY_VISIBLE_ACCOUNTS_FOR_GUESTS,
        }))
      : [];

  // 유효한 이미지를 가진 계정들만 필터링
  const filteredAccounts = useMemo(() => {
    // 모든 계정 데이터 수집
    const allAccounts = gridAccounts?.pages?.flatMap((page) =>
      page.accounts.filter(account =>
        isValidImageUrl(account.supabase_profile_pic_url) &&
        isValidImageUrl(account.supabase_latest_post_url)
      )
    ) || [];

    // username 기준으로 중복 제거
    const uniqueAccounts = Array.from(
      new Map(allAccounts.map(account => [account.username, account])).values()
    );

    return uniqueAccounts;
  }, [gridAccounts]);

  useEffect(() => {
    if (selectedProfile) {
      console.log("렌더링될 게시물 데이터:", recentPosts);
      console.log("게시물 개수:", recentPosts.length);
    }
  }, [recentPosts, selectedProfile]);

  const handleProfileClick = useCallback(
    (profile: InstantChatAccount, index: number) => {
      if (!session && index >= FULLY_VISIBLE_ACCOUNTS_FOR_GUESTS) {
        toast({
          title: "로그인 필요",
          description: "이 계정과 대화하려면 로그인이 필요합니다.",
        });
        navigate("/auth", { state: { returnTo: "/instant-chat" } });
        return;
      }
      setRecentPosts([]);
      setIsLoadingPosts(true);
      setSelectedProfile(profile);
      setShowProfileDetail(true);

      // 이미지 URL 최적화 함수 - 무결성 이슈로 인해 일시 비활성화
      // 처리를 단순화하고 원본 이미지 URL 사용

      const fetchRecentPosts = async (): Promise<void> => {
        if (!profile?.username) {
          setRecentPosts([]);
          setIsLoadingPosts(false);
          return;
        }
        setIsLoadingPosts(true);
        try {
          // 필요한 필드만 선택적으로 요청
          const { data, error } = await supabase
            .from("public_instagram_posts")
            .select("id, imagekit_url, timestamp, caption, likes_count")
            .eq("instagram_username", profile.username)
            .order("timestamp", { ascending: false })
            .limit(3);

          if (error) {
            // 오류 발생 시 조용히 처리
            setRecentPosts([]);
            setIsLoadingPosts(false);
            return;
          }

          // 1. 유효한 이미지 URL을 가진 게시물만 필터링
          const validPosts = data?.filter(post =>
            post.imagekit_url &&
            post.imagekit_url.trim() !== "" &&
            isValidImageUrl(post.imagekit_url)
          ) || [];

          // 2. 필터링된 게시물이 없는 경우에만 프로필의 최근 게시물 URL 확인
          if (validPosts.length === 0) {
            if (profile.supabase_latest_post_url &&
              profile.supabase_latest_post_url.trim() !== "" &&
              isValidImageUrl(profile.supabase_latest_post_url)) {

              // 대체 이미지가 유효한 경우에만 추가
              const singlePost = {
                id: `latest-${profile.id}-${Date.now()}`,
                imagekit_url: profile.supabase_latest_post_url,
                caption: "",
                likes_count: 0,
                timestamp: new Date().toISOString(),
              };
              setRecentPosts([singlePost]);
            } else {
              // 유효한 이미지가 없는 경우 빈 배열로 설정
              setRecentPosts([]);
            }
          } else {
            // 3. 유효한 게시물이 있는 경우 중복 제거 후 설정
            // ID 기준으로 중복 제거
            const uniquePosts = Array.from(
              new Map(validPosts.map(post => [post.id, post])).values()
            );
            setRecentPosts(uniquePosts);
          }
        } catch (error) {
          // 오류 발생 시 빈 배열로 설정
          setRecentPosts([]);
        } finally {
          setIsLoadingPosts(false);
        }
      };
      fetchRecentPosts();
    },
    [session, toast, navigate]
  );

  const handleCloseProfileDetail = () => {
    setShowProfileDetail(false);
    setRecentPosts([]);
    setIsLoadingPosts(false);
  };

  const handleStartChat = async () => {
    if (!selectedProfile) {
      toast({
        title: "계정을 선택해주세요",
        description: "대화를 시작하려면 먼저 계정을 선택해야 합니다.",
        variant: "destructive",
      });
      return;
    }

    if (isSaving) return;
    setIsSaving(true);

    // 로그인 여부 확인
    if (!session) {
      toast({
        title: "로그인 필요",
        description:
          "대화를 시작하려면 로그인이 필요합니다. 로그인 페이지로 이동합니다.",
      });
      navigate("/auth", { state: { returnTo: "/" } });
      setIsSaving(false);
      return;
    }

    // 계정 쿼터 확인
    if (!isLoadingQuota && quotaInfo) {
      if (quotaInfo.can_add === false) {
        toast({
          title: "계정 연결 제한 초과",
          description: `현재 요금제(${quotaInfo.subscription_tier})에서는 최대 ${quotaInfo.quota === -1 ? '무제한' : quotaInfo.quota}개의 계정을 연결할 수 있습니다. 요금제를 업그레이드하세요.`,
          variant: "destructive",
        });
        setCurrentPlan(quotaInfo.subscription_tier || "free");
        setShowQuotaExceededAlert(true);
        setIsSaving(false);
        return;
      }
    }

    try {
      console.log("인스타그램 계정 저장 시도:", selectedProfile.username);
      const copyResult = await copyPublicAccountToSaved(
        selectedProfile.id
      ) as CopyPublicAccountResult;
      if (copyResult.quotaExceeded) {
        toast({
          title: "계정 연결 한도 도달",
          description: "현재 구독으로는 더 이상 계정을 추가할 수 없습니다.",
          variant: "destructive",
        });
        setCurrentPlan(copyResult.currentPlan || "free");
        setShowQuotaExceededAlert(true);
        setIsSaving(false);
        navigate("/pricing");
        return;
      }
      if (!copyResult.success) {
        toast({
          title: "계정 처리 실패",
          description: copyResult.message,
          variant: "destructive",
        });
        setIsSaving(false);
        return;
      }
      setTimeout(() => {
        navigate("/search", {
          state: {
            username: selectedProfile.username,
            direct: "chat",
            profile: selectedProfile.profile_data || {},
          },
        });
      }, 300);
    } catch (error: unknown) {
      // 오류 로그 출력 제거
      // console.error("Error starting chat:", error);
      type ErrorWithMessage = { message: string };
      function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
        return (
          typeof error === 'object' &&
          error !== null &&
          'message' in error &&
          typeof (error as Record<string, unknown>).message === 'string'
        );
      }
      if (isErrorWithMessage(error) && error.message.includes("account_quota_exceeded")) {
        toast({
          title: "계정 연결 한도 도달",
          description: "현재 구독으로는 더 이상 계정을 추가할 수 없습니다.",
          variant: "destructive",
        });
        let plan = "free";
        if (error.message.includes("basic")) plan = "basic";
        else if (error.message.includes("premium")) plan = "premium";
        setCurrentPlan(plan);
        setShowQuotaExceededAlert(true);
        navigate("/pricing");
      } else {
        toast({
          title: "오류",
          description: "대화를 시작하는 중 문제가 발생했습니다.",
          variant: "destructive",
        });
      }
      setIsSaving(false);
    }
  };

  const handleCarouselProfileSelect = (profile: CarouselProfile): void => {
    if (!session && profile.locked) {
      toast({
        title: "로그인 필요",
        description: "이 계정과 대화하려면 로그인이 필요합니다." as React.ReactNode,
        variant: "default"
      });
      navigate("/auth", { state: { returnTo: "/instant-chat" } });
      return;
    }
    const allAccounts = [
      ...(carouselAccounts?.filter(account =>
        isValidImageUrl(account.supabase_profile_pic_url) &&
        isValidImageUrl(account.supabase_latest_post_url)
      ) || []),
      ...(gridAccounts?.pages?.flatMap((page) =>
        page.accounts.filter(account =>
          isValidImageUrl(account.supabase_profile_pic_url) &&
          isValidImageUrl(account.supabase_latest_post_url)
        )
      ) || []),
    ];
    const selectedAccount = allAccounts.find(
      (account) => account.username === profile.username
    );
    if (selectedAccount) {
      handleProfileClick(selectedAccount, 0);
    }
  };

  const formatCount = (count?: number | boolean): string => {
    if (count === undefined || count === null || typeof count === 'boolean') return "0";
    if (count >= 1000000) return `${(count / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1).replace(/\.0$/, "")}K`;
    return count.toString();
  };

  const PostShimmer = () => (
    <div className="grid grid-cols-3 gap-2 w-full">
      {Array(3).fill(0).map((_, i) => (
        <div
          key={`shimmer-${i}`}
          className="relative aspect-square rounded-xl overflow-hidden shadow-md border bg-gradient-to-r from-background via-muted to-background"
          style={{ backgroundSize: "200% 100%", animation: "shimmer 2s infinite linear" }}
        />
      ))}
    </div>
  );

  useEffect(() => {
    if (!document.getElementById("shimmer-animation-style")) {
      const style = document.createElement("style");
      style.id = "shimmer-animation-style";
      style.innerHTML = `
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: 0% 0; }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // 인스타그램 프로필 바로가기 함수
  const openInstagramProfile = () => {
    if (selectedProfile) {
      const link = document.createElement("a");
      link.href = `https://www.instagram.com/${selectedProfile.username}`;
      link.rel = "noreferrer noopener";
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-background to-background/80">
      <motion.div
        className="fixed inset-0 -z-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="absolute inset-0 bg-grid-pattern opacity-30"></div>
        <div className="absolute top-0 left-0 w-full h-[50vh] bg-gradient-to-br from-primary/10 via-accent/5 to-transparent"></div>
        <div className="absolute bottom-0 right-0 w-full h-[50vh] bg-gradient-to-tl from-primary/5 via-accent/10 to-transparent"></div>
      </motion.div>

      <header className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur-lg">
        <div className="container flex h-16 items-center justify-between">
          <motion.div
            className="flex items-center gap-2"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h1 className="text-xl font-bold gradient-heading">
              Insta Chat AI
            </h1>
            <Badge variant="outline" className="ml-2 hidden sm:inline-flex">
              인기 계정
            </Badge>
          </motion.div>
          <motion.div
            className="flex items-center gap-2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {session && (
              <div className="flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                <Zap className="h-3 w-3" />
                <span>{availableCredits ?? "..."} 크레딧</span>
              </div>
            )}
            <ThemeToggle />
          </motion.div>
        </div>
      </header>

      <main className="container flex-1 py-8 pb-24">
        {isCarouselLoading ? (
          <motion.div
            className="flex h-full items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Preloader size="medium" />
          </motion.div>
        ) : (
          <div>
            {carouselAccounts && carouselAccounts.length > 0 && (
              <motion.section
                className="mb-10"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.7,
                  delay: 0.3,
                  type: "spring",
                  stiffness: 100,
                  damping: 15,
                }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                    인기 계정
                  </h2>
                </div>
                <ProfileCarousel
                  profiles={carouselProfiles}
                  onSelect={handleCarouselProfileSelect}
                  isLoading={isCarouselLoading}
                  guestMode={!session}
                  isLoadingPosts={isLoadingPosts}
                  recentPosts={recentPosts}
                />
              </motion.section>
            )}
            <motion.div
              className="border-t pt-5 pb-5"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <h2 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary/90 to-accent/90">
                모든 인스타그램 계정
              </h2>
            </motion.div>

            <div className="relative">
              <motion.div
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.8 }}
              >
                <AnimatePresence initial={false}>
                  {filteredAccounts.map((account, index) => {
                    if (!account) return null;
                    const isLockedForGuest =
                      !session && index >= FULLY_VISIBLE_ACCOUNTS_FOR_GUESTS;
                    const profilePic =
                      account.supabase_profile_pic_url ||
                      account.profile_data?.profilePicUrl;
                    const latestPost = account.supabase_latest_post_url;
                    const username = account.username || "알 수 없음";
                    const followers = formatCount(
                      account.profile_data?.followersCount
                    );

                    return (
                      <motion.div
                        key={account.id}
                        layout
                        initial={{ opacity: 0, y: 20 }}
                        animate={{
                          opacity: isLockedForGuest ? 0.7 : 1,
                          y: 0,
                          scale: 1,
                        }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{
                          duration: 0.5,
                          delay: (index % 10) * 0.05,
                          type: "spring",
                          stiffness: 200,
                          damping: 20,
                        }}
                        whileHover={{
                          y: -5,
                          scale: 1.03,
                          transition: { duration: 0.2 },
                          boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                        }}
                        className={`cursor-pointer overflow-hidden rounded-xl ${isLockedForGuest
                          ? "relative filter grayscale-[50%]"
                          : ""
                          }`}
                        onClick={() => handleProfileClick(account, index)}
                        title={
                          isLockedForGuest
                            ? "로그인 후 확인 가능"
                            : `@${username} 프로필 보기`
                        }
                      >
                        <div className="relative aspect-square overflow-hidden rounded-xl border shadow-md hover:shadow-xl transition-shadow duration-300 bg-muted">
                          <img
                            src={latestPost || "/placeholder-post.svg"}
                            alt={
                              latestPost
                                ? `${username}의 최근 게시물`
                                : "게시물 없음"
                            }
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                            loading="lazy"
                            onError={(e): void => {
                              // 이미지 로드 실패 시 placeholder 이미지로 대체
                              (e.currentTarget as HTMLImageElement).src = "/placeholder-post.svg";
                              // 추가 스타일 조정
                              (e.currentTarget as HTMLImageElement).classList.add('object-contain', 'bg-muted/50');
                            }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none" />

                          {isLockedForGuest && (
                            <motion.div
                              className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ duration: 0.3 }}
                            >
                              <motion.div
                                className="rounded-full bg-white/20 p-3"
                                animate={{
                                  scale: [1, 1.1, 1],
                                  boxShadow: [
                                    "0 0 0 rgba(255,255,255,0.4)",
                                    "0 0 20px rgba(255,255,255,0.6)",
                                    "0 0 0 rgba(255,255,255,0.4)",
                                  ],
                                }}
                                transition={{
                                  duration: 2,
                                  repeat: Infinity,
                                  repeatType: "loop",
                                }}
                              >
                                <Lock className="h-6 w-6 text-white" />
                              </motion.div>
                            </motion.div>
                          )}

                          <div className="absolute bottom-0 left-0 right-0 p-3 flex items-center gap-2 pointer-events-none">
                            <Avatar className="h-8 w-8 ring-2 ring-white/30 flex-shrink-0">
                              <AvatarImage
                                src={profilePic}
                                alt={`${username} 프로필 사진`}
                              />
                              <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white">
                                {username.charAt(0).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div className="overflow-hidden">
                              <p className="font-medium text-white truncate text-shadow">
                                @{username}
                              </p>
                              <p className="text-[10px] text-white/80 truncate text-shadow">
                                {followers} 팔로워
                              </p>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </motion.div>

              {isFetchingNextPage && (
                <motion.div
                  className="flex justify-center mt-8 mb-4"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <Preloader size="small" />
                </motion.div>
              )}
              <div ref={observerTarget} className="h-4 w-full"></div>
            </div>
          </div>
        )}
      </main>

      <AnimatePresence>
        {showProfileDetail && selectedProfile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-md p-0 overflow-hidden"
            onClick={() => handleCloseProfileDetail()}
            aria-modal="true"
            role="dialog"
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{
                type: "spring",
                damping: 35,
                stiffness: 400,
                mass: 1,
              }}
              className="w-full max-w-md bg-background/95 backdrop-blur-xl shadow-2xl rounded-t-3xl max-h-[80vh] md:max-h-[85vh] overflow-y-auto flex flex-col"
              onClick={(e) => e.stopPropagation()}
              aria-labelledby="profile-detail-title"
            >
              <div className="sticky top-0 z-10 flex justify-between items-center p-4 bg-background/90 backdrop-blur-sm border-b rounded-t-3xl">
                <div className="w-8 h-8"></div>
                <motion.div
                  className="flex-grow text-center"
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.3 }}
                >
                  <motion.div
                    className="inline-block w-10 h-1.5 bg-muted rounded-full"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatType: "reverse",
                    }}
                  ></motion.div>
                </motion.div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleCloseProfileDetail()}
                  className="h-9 w-9 rounded-full hover:bg-muted flex-shrink-0 transition-all duration-300 hover:rotate-90"
                  aria-label="닫기"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <div className="px-6 pb-8 pt-4 flex flex-col items-center flex-grow overflow-y-auto"> {/* 버튼들이 이 div 안에 오도록 수정 */}
                <motion.div
                  className="relative mb-5"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{
                    type: "spring",
                    stiffness: a300,
                    damping: 20,
                    delay: 0.1,
                  }}
                >
                  <Avatar className="h-24 w-24 border-4 border-background shadow-xl">
                    <AvatarImage
                      src={
                        selectedProfile.supabase_profile_pic_url ||
                        selectedProfile.profile_data?.profilePicUrl
                      }
                      alt={`${selectedProfile.username || "사용자"
                        } 프로필 사진`}
                    />
                    <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white text-xl">
                      {(selectedProfile.username || "?")
                        .charAt(0)
                        .toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  {selectedProfile.profile_data?.isVerified && (
                    <motion.div
                      className="absolute bottom-1 right-1 bg-blue-500 text-white p-1 rounded-full ring-2 ring-background"
                      title="인증된 계정"
                      animate={{
                        scale: [1, 1.1, 1],
                        boxShadow: [
                          "0 0 0 rgba(59, 130, 246, 0.4)",
                          "0 0 8px rgba(59, 130, 246, 0.7)",
                          "0 0 0 rgba(59, 130, 246, 0.4)",
                        ],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        repeatType: "loop",
                      }}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        stroke="none"
                      >
                        <path d="M22.46 6.7L12.73 1.64c-.6-.33-1.34-.33-1.94 0L1.54 6.7c-.6.33-.99 1.02-.99 1.76v7.08c0 .74.4 1.43.99 1.76l9.73 5.06c.6.33 1.34.33 1.94 0l9.73-5.06c.6-.33.99-1.02.99-1.76V8.46c0-.74-.4-1.43-.99-1.76zm-10.1 10.64l-4.03-4.11 1.46-1.42 2.57 2.63 5.45-5.55 1.46 1.42-6.91 7.03z" />
                      </svg>
                    </motion.div>
                  )}
                </motion.div>

                <motion.div
                  className="text-center mb-5"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <motion.h2
                    id="profile-detail-title"
                    className="text-2xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent"
                  >
                    @{selectedProfile.username || "알 수 없는 사용자"}
                  </motion.h2>
                  <motion.p
                    className="text-muted-foreground mt-1"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    {selectedProfile.display_name ||
                      selectedProfile.profile_data?.fullName ||
                      ""}
                  </motion.p>
                </motion.div>

                <motion.div
                  className="grid grid-cols-3 gap-3 w-full mb-6"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.5,
                    delay: 0.3,
                    staggerChildren: 0.1,
                    delayChildren: 0.4,
                  }}
                >
                  <motion.div
                    className="text-center p-2 rounded-lg border bg-card/50 hover:bg-card/80 transition-colors duration-300"
                    whileHover={{
                      y: -2,
                      boxShadow: "0 8px 15px rgba(0,0,0,0.1)",
                    }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <span className="block text-lg font-semibold bg-clip-text text-transparent bg-gradient-to-r from-primary/90 to-primary">
                      {formatCount(selectedProfile.profile_data?.postsCount)}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      게시물
                    </span>
                  </motion.div>
                  <motion.div
                    className="text-center p-2 rounded-lg border bg-card/50 hover:bg-card/80 transition-colors duration-300"
                    whileHover={{
                      y: -2,
                      boxShadow: "0 8px 15px rgba(0,0,0,0.1)",
                    }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <span className="block text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary/80 to-accent/90">
                      {formatCount(
                        selectedProfile.profile_data?.followersCount
                      )}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      팔로워
                    </span>
                  </motion.div>
                  <motion.div
                    className="text-center p-2 rounded-lg border bg-card/50 hover:bg-card/80 transition-colors duration-300"
                    whileHover={{
                      y: -2,
                      boxShadow: "0 8px 15px rgba(0,0,0,0.1)",
                    }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <span className="block text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-accent/90 to-accent/70">
                      {formatCount(selectedProfile.profile_data?.followsCount)}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      팔로잉
                    </span>
                  </motion.div>
                </motion.div>

                {selectedProfile.profile_data?.biography && (
                  <motion.div
                    className="w-full mb-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                  >
                    <h3 className="text-sm font-semibold text-foreground mb-2">
                      소개
                    </h3>
                    <p className="text-sm whitespace-pre-wrap rounded-md bg-muted/50 p-3 break-words leading-relaxed backdrop-blur-sm border border-muted">
                      {selectedProfile.profile_data.biography}
                    </p>
                  </motion.div>
                )}

                <motion.div
                  className="w-full mb-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  <h3 className="text-sm font-semibold text-foreground mb-2">
                    최근 게시물
                  </h3>
                  {isLoadingPosts ? (
                    <PostShimmer />
                  ) : recentPosts.length === 0 ? (
                    <div className="w-full p-4 bg-muted rounded-lg flex items-center justify-center">
                      <p className="text-sm text-muted-foreground">최근 게시물이 없습니다.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-2 w-full">
                      {recentPosts.map((post) => (
                        <div
                          key={post.id}
                          className="relative aspect-square rounded-xl overflow-hidden shadow-md border transition-transform duration-500 hover:scale-[1.02] hover:shadow-lg"
                        >
                          {post.imagekit_url ? (
                            <img
                              src={post.imagekit_url}
                              alt={`${selectedProfile.username || "사용자"}의 최근 게시물 이미지`}
                              className="object-cover w-full h-full transition-transform duration-700 hover:scale-110"
                              loading="lazy"
                              decoding="async"
                              onError={(e): void => {
                                // 이미지 로드 실패 시 placeholder 이미지로 대체하고 부모 요소 숨김
                                (e.currentTarget as HTMLImageElement).src = "/placeholder-post.svg";
                                // 부모 DOM 엘리먼트 숨김 처리
                                const parent = (e.currentTarget as HTMLImageElement).closest('.aspect-square');
                                if (parent) {
                                  (parent as HTMLElement).style.display = 'none';
                                }
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-muted">
                              <p className="text-xs text-muted-foreground">이미지 없음</p>
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300">
                            <div className="absolute bottom-3 left-3 text-white text-shadow text-xs">
                              {post.likes_count ? `좋아요 ${post.likes_count}개` : "최근 게시물"}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>

                {/* 적용된 버튼 컴포넌트 */}
                <BottomButtons
                  handleStartChat={handleStartChat}
                  isSaving={isSaving}
                  session={session}
                  selectedProfile={selectedProfile}
                  openInstagramProfile={openInstagramProfile}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <QuotaExceededDialog
        open={showQuotaExceededAlert}
        onOpenChange={setShowQuotaExceededAlert}
        currentPlan={currentPlan}
      />
      <MobileNav />
    </div>
  );
}